import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import {
  LeaveRequestDetailsDialog,
  LeaveRequestListCard,
} from '../../components/Teacher/LeaveRequestDetails';
import LeaveApprovalDialog from '../../components/Teacher/LeaveApprovalDialog';
import { useAuth } from '../../contexts/AuthContext';

const ManagerLeaveApprovals = () => {
  const { canApproveLeave, canManageLeaveCoverage } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = React.useState(0);
  const [notesById, setNotesById] = React.useState({});
  const [detailRequest, setDetailRequest] = React.useState(null);
  const [approveRequest, setApproveRequest] = React.useState(null);
  const [coverageRequest, setCoverageRequest] = React.useState(null);

  const pendingQuery = useQuery(['managerLeavePending'], async () => {
    const res = await teacherModuleAPI.getPendingLeaveRequests();
    return res.data?.data || [];
  });

  const allQuery = useQuery(
    ['managerLeaveAll'],
    async () => {
      const res = await teacherModuleAPI.getAllLeaveRequests();
      return res.data?.data || [];
    },
    { enabled: tab === 1 }
  );

  const decideMutation = useMutation(
    ({ id, status, decisionNotes, batchSubstitutes, batchMerges, batchCancellations, batchNoActions }) =>
      teacherModuleAPI.decideLeaveRequest(id, {
        status,
        decisionNotes,
        batchSubstitutes,
        batchMerges,
        batchCancellations,
        batchNoActions,
      }),
    {
      onSuccess: (res) => {
        toast.success(
          res.data?.message || `Leave ${res.data?.data?.status === 'approved' ? 'approved' : 'updated'}`
        );
        setApproveRequest(null);
        queryClient.invalidateQueries(['managerLeavePending']);
        queryClient.invalidateQueries(['managerLeaveAll']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to update request'),
    }
  );

  const coverageMutation = useMutation(
    ({ id, ...payload }) => teacherModuleAPI.updateLeaveBatchCoverage(id, payload),
    {
      onSuccess: (res) => {
        toast.success(res.data?.message || 'Batch coverage saved');
        setCoverageRequest(null);
        queryClient.invalidateQueries(['managerLeavePending']);
        queryClient.invalidateQueries(['managerLeaveAll']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to save coverage'),
    }
  );

  const renderDecisionButtons = (r) => (
    <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
      {canManageLeaveCoverage() ? (
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={coverageMutation.isLoading || decideMutation.isLoading}
          onClick={() => setCoverageRequest(r)}
        >
          Assign coverage
        </Button>
      ) : null}
      {canApproveLeave() ? (
        <>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={decideMutation.isLoading}
            onClick={() => setApproveRequest(r)}
          >
            Approve
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={decideMutation.isLoading}
            onClick={() =>
              decideMutation.mutate({
                id: r.id,
                status: 'rejected',
                decisionNotes: notesById[r.id] || '',
              })
            }
          >
            Reject
          </Button>
        </>
      ) : null}
    </Stack>
  );

  const renderPendingCard = (r) => (
    <LeaveRequestListCard
      key={r.id}
      request={r}
      showTeacher
      onViewDetails={setDetailRequest}
      headerActions={renderDecisionButtons(r)}
      footer={
        canApproveLeave() ? (
          <TextField
            size="small"
            label="Decision notes (optional)"
            value={notesById[r.id] || ''}
            onChange={(e) => setNotesById((prev) => ({ ...prev, [r.id]: e.target.value }))}
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
          />
        ) : canManageLeaveCoverage() ? (
          <Typography variant="caption" color="text.secondary">
            You can assign substitutes and batch coverage. A teacher supervisor must approve or reject the leave.
          </Typography>
        ) : null
      }
    />
  );

  const historyList = tab === 1 ? allQuery.data || [] : [];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {canApproveLeave() ? 'Leave Approvals' : 'Leave & Substitutes'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {canApproveLeave()
          ? 'Review pending requests by teacher name and track approved or rejected leave.'
          : 'View teacher leave requests and assign batch substitutes. Leave approval is done by a teacher supervisor.'}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Pending (${(pendingQuery.data || []).length})`} />
        <Tab label="All requests" />
      </Tabs>

      {tab === 0 ? (
        <Box>
          {(pendingQuery.data || []).map((r) => renderPendingCard(r))}
          {(pendingQuery.data || []).length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No pending leave requests.</Typography>
            </Paper>
          ) : null}
        </Box>
      ) : (
        <Box>
          {allQuery.isLoading ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Loading leave history…
            </Typography>
          ) : null}
          {!allQuery.isLoading && historyList.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No leave requests found.</Typography>
            </Paper>
          ) : null}
          {!allQuery.isLoading
            ? historyList.map((r) =>
                r.status === 'pending' ? (
                  <Box key={r.id}>{renderPendingCard(r)}</Box>
                ) : (
                  <LeaveRequestListCard
                    key={r.id}
                    request={r}
                    showTeacher
                    onViewDetails={setDetailRequest}
                  />
                )
              )
            : null}
        </Box>
      )}

      <LeaveRequestDetailsDialog
        open={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        request={detailRequest}
        showTeacher
      />

      <LeaveApprovalDialog
        open={Boolean(approveRequest)}
        onClose={() => setApproveRequest(null)}
        request={approveRequest}
        isSubmitting={decideMutation.isLoading}
        onConfirm={(payload) =>
          decideMutation.mutate({
            id: approveRequest.id,
            ...payload,
            decisionNotes: payload.decisionNotes || notesById[approveRequest.id] || '',
          })
        }
      />

      <LeaveApprovalDialog
        open={Boolean(coverageRequest)}
        onClose={() => setCoverageRequest(null)}
        request={coverageRequest}
        coverageOnly
        isSubmitting={coverageMutation.isLoading}
        onConfirm={(payload) =>
          coverageMutation.mutate({
            id: coverageRequest.id,
            batchSubstitutes: payload.batchSubstitutes,
            batchMerges: payload.batchMerges,
            batchCancellations: payload.batchCancellations,
            batchNoActions: payload.batchNoActions,
          })
        }
      />
    </Box>
  );
};

export default ManagerLeaveApprovals;
